/**
 * Accounting module — Journal / Trial Balance / P&L.
 * Session B (2026-07-21) — double-entry bookkeeping.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { BookOpen, Scale, TrendingUp, Plus, Trash2, Loader2, Upload } from "lucide-react";
import BankStatementImport from "@/components/accounting/BankStatementImport";

const TABS = [
  { id: "journal", label: "Journal", icon: BookOpen },
  { id: "trial",   label: "Trial balance", icon: Scale },
  { id: "pnl",     label: "Profit & Loss", icon: TrendingUp },
  { id: "import",  label: "Import bank",  icon: Upload },
];

const fmt = (n) => Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AccountingModule() {
  const [tab, setTab] = useState("journal");
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/accounting/accounts`, { withCredentials: true })
      .then((r) => setAccounts(r.data.accounts || []))
      .catch(() => { /* seeded automatically on first call */ });
  }, []);
  return (
    <div className="space-y-6" data-testid="accounting-module">
      <header>
        <h1 className="text-[26px] font-bold tracking-tight text-black notranslate" translate="no">Accounting</h1>
        <p className="text-[14px] text-[#666] mt-1">Double-entry journal, trial balance, and profit &amp; loss for your workspace.</p>
      </header>
      <nav className="flex flex-wrap gap-1 border-b border-[#eee]">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`acc-tab-${t.id}`}
            className={`px-4 py-2.5 text-[13.5px] font-medium border-b-2 flex items-center gap-2 ${
              tab === t.id ? "border-[#202B60] text-[#202B60]" : "border-transparent text-[#666] hover:text-black"}`}>
            <t.icon size={15} /> <span className="notranslate" translate="no">{t.label}</span>
          </button>
        ))}
      </nav>
      <div>
        {tab === "journal" && <JournalPanel accounts={accounts} />}
        {tab === "trial" && <TrialBalancePanel />}
        {tab === "pnl" && <PnLPanel />}
        {tab === "import" && <BankStatementImport accounts={accounts} />}
      </div>
    </div>
  );
}

function JournalPanel({ accounts }) {
  const postingPending = useRef(false);
  const [posting, setPosting] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState([
    { account_code: "", debit: "", credit: "" },
    { account_code: "", debit: "", credit: "" },
  ]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/accounting/journal-entries`, { withCredentials: true }); setEntries(data.entries || []); }
    catch { toast.error("Failed to load journal."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const totals = () => {
    const d = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const c = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    return { d, c, balanced: Math.round(d * 100) === Math.round(c * 100) && d > 0 };
  };

  const create = async () => {
    if (postingPending.current) return;
    const parsedLines = [];
    for (const [index, line] of lines.entries()) {
      const debit = line.debit === "" ? 0 : Number(line.debit);
      const credit = line.credit === "" ? 0 : Number(line.credit);
      if (!line.account_code || !Number.isFinite(debit) || !Number.isFinite(credit)
          || debit < 0 || credit < 0 || (debit > 0) === (credit > 0)) {
        return toast.error(`Line ${index + 1}: choose an account and enter one valid positive debit or credit amount.`);
      }
      parsedLines.push({ account_code: line.account_code, debit, credit });
    }
    const debitTotal = parsedLines.reduce((sum, line) => sum + line.debit, 0);
    const creditTotal = parsedLines.reduce((sum, line) => sum + line.credit, 0);
    if (!Number.isFinite(debitTotal) || !Number.isFinite(creditTotal) || debitTotal <= 0
        || Math.round(debitTotal * 100) !== Math.round(creditTotal * 100)) {
      return toast.error("The journal entry must balance before posting.");
    }
    postingPending.current = true;
    setPosting(true);
    try {
      await axios.post(`${API}/accounting/journal-entries`, {
        date: entryDate,
        description,
        lines: parsedLines,
      }, { withCredentials: true });
      toast.success("Journal entry posted.");
      setShowForm(false);
      setDescription("");
      setLines([{ account_code: "", debit: "", credit: "" }, { account_code: "", debit: "", credit: "" }]);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Posting was not confirmed. Check the journal before trying again."); }
    finally { postingPending.current = false; setPosting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this journal entry?")) return;
    try { await axios.delete(`${API}/accounting/journal-entries/${id}`, { withCredentials: true }); load(); } catch { /* noop */ }
  };

  const t = totals();
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold">{entries.length} entr{entries.length === 1 ? "y" : "ies"}</h2>
        <button disabled={posting} onClick={() => setShowForm(!showForm)} className="zy-btn-primary flex items-center gap-1.5 text-[13px]" data-testid="acc-journal-add-btn"><Plus size={14} /> New entry</button>
      </div>
      {showForm && (
        <fieldset disabled={posting} aria-busy={posting} className="border border-[#eee] rounded-xl p-4 bg-white space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="text-[13px] px-3 py-2 border border-[#eee] rounded-md" data-testid="acc-journal-date" />
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" className="text-[13px] px-3 py-2 border border-[#eee] rounded-md" data-testid="acc-journal-desc" />
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-[#F4F6FB] text-[#555]">
              <tr><th className="text-left px-2 py-1.5">Account</th><th className="text-right px-2 py-1.5">Debit</th><th className="text-right px-2 py-1.5">Credit</th></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td className="px-2 py-1">
                    <select value={l.account_code} onChange={(e) => { const value = e.target.value; setLines((current) => current.map((line, index) => index === i ? { ...line, account_code: value } : line)); }} className="w-full text-[13px] px-2 py-1.5 border border-[#eee] rounded-md" data-testid={`acc-journal-line-${i}-acct`}>
                      <option value="">Select account…</option>
                      {accounts.map((a) => <option key={a.id} value={a.code}>{a.code} · {a.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1"><input type="number" step="0.01" value={l.debit} onChange={(e) => { const value = e.target.value; setLines((current) => current.map((line, index) => index === i ? { ...line, debit: value, ...(value ? { credit: "" } : {}) } : line)); }} className="w-full text-right text-[13px] px-2 py-1.5 border border-[#eee] rounded-md" data-testid={`acc-journal-line-${i}-debit`} /></td>
                  <td className="px-2 py-1"><input type="number" step="0.01" value={l.credit} onChange={(e) => { const value = e.target.value; setLines((current) => current.map((line, index) => index === i ? { ...line, credit: value, ...(value ? { debit: "" } : {}) } : line)); }} className="w-full text-right text-[13px] px-2 py-1.5 border border-[#eee] rounded-md" data-testid={`acc-journal-line-${i}-credit`} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#eee] font-semibold">
                <td className="px-2 py-1.5">Total</td>
                <td className="px-2 py-1.5 text-right">€{fmt(t.d)}</td>
                <td className="px-2 py-1.5 text-right">€{fmt(t.c)}</td>
              </tr>
            </tfoot>
          </table>
          <div className="flex justify-between items-center">
            <button onClick={() => setLines((current) => [...current, { account_code: "", debit: "", credit: "" }])} className="text-[13px] text-[#202B60]" data-testid="acc-journal-add-line">+ Add line</button>
            <span className={`text-[12px] font-semibold ${t.balanced ? "text-[#0F7A2A]" : "text-[#a10404]"}`} data-testid="acc-journal-balanced">
              {t.balanced ? "✓ Balanced" : "Not balanced"}
            </span>
          </div>
          <div className="flex gap-2 justify-end">
            <button disabled={posting} onClick={() => setShowForm(false)} className="px-3 py-2 text-[13px] text-[#666]">Cancel</button>
            <button onClick={create} disabled={posting || !t.balanced} className="zy-btn-primary text-[13px] disabled:opacity-50 notranslate" translate="no" data-testid="acc-journal-save">{posting ? "Posting…" : "Post entry"}</button>
          </div>
        </fieldset>
      )}
      {loading ? <Loader2 className="animate-spin text-[#999]" /> : entries.length === 0 ? (
        <div className="border border-dashed border-[#e5e7ee] rounded-xl p-10 text-center text-[#888]">No entries yet. Click "New entry" to record your first transaction.</div>
      ) : (
        <div className="border border-[#eee] rounded-xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F4F6FB] text-[#555]"><tr><th className="text-left px-4 py-2.5">#</th><th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">Description</th><th className="text-right px-4 py-2.5">Debit</th><th className="text-right px-4 py-2.5">Credit</th><th /></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-[#eee]" data-testid={`acc-journal-row-${e.id}`}>
                  <td className="px-4 py-2.5 text-[#888]">#{e.entry_no}</td>
                  <td className="px-4 py-2.5 text-[#555]">{e.date}</td>
                  <td className="px-4 py-2.5 text-[#111]">{e.description || "—"}<div className="text-[11.5px] text-[#888]">{e.lines.map((l) => `${l.account_code}`).join(" · ")}</div></td>
                  <td className="px-4 py-2.5 text-right text-[#555]">€{fmt(e.total_debit)}</td>
                  <td className="px-4 py-2.5 text-right text-[#555]">€{fmt(e.total_credit)}</td>
                  <td className="px-4 py-2.5 text-right"><button onClick={() => remove(e.id)} className="text-[#c00]"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function validReportNumber(value) {
  return (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) &&
    Number.isFinite(Number(value));
}

function readTrialBalance(value) {
  if (!value || !Array.isArray(value.rows) || typeof value.balanced !== "boolean" ||
      !validReportNumber(value.total_debit) || !validReportNumber(value.total_credit) ||
      value.rows.some((row) => !row || typeof row !== "object" ||
        ["account_id", "account_code", "account_name", "account_type"].some((key) => typeof row[key] !== "string") ||
        !validReportNumber(row.debit) || !validReportNumber(row.credit))) {
    throw new Error("Incomplete trial balance.");
  }
  return value;
}

function readProfitAndLoss(value) {
  if (!value || !Array.isArray(value.revenue) || !Array.isArray(value.expenses) ||
      ["total_revenue", "total_expenses", "net_income"].some((key) => !validReportNumber(value[key])) ||
      [...value.revenue, ...value.expenses].some((row) => !row || typeof row !== "object" ||
        ["account_id", "account_code", "account_name"].some((key) => typeof row[key] !== "string") ||
        !validReportNumber(row.amount))) {
    throw new Error("Incomplete profit and loss report.");
  }
  return value;
}

function useAccountingReport(endpoint, readReport) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [appliedParams, setAppliedParams] = useState({});
  const sequence = useRef(0);
  const load = async (params = {}) => {
    const request = ++sequence.current;
    setLoading(true);
    setLoadError("");
    setData(null);
    try {
      const { data: response } = await axios.get(`${API}/accounting/${endpoint}`, {
        params, withCredentials: true,
      });
      if (request !== sequence.current) return;
      setData(readReport(response));
      setAppliedParams({ ...params });
    } catch {
      if (request === sequence.current) {
        setLoadError("The report could not be loaded completely. No figures are shown. Please try again.");
      }
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  };
  useEffect(() => {
    load();
    return () => { sequence.current += 1; };
    // endpoint and readReport are stable for this panel; load is invoked explicitly for refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, readReport]);
  return { data, loading, loadError, appliedParams, load };
}

function TrialBalancePanel() {
  const { data, loading, loadError, load } = useAccountingReport("trial-balance", readTrialBalance);
  if (loading) return <p role="status" className="text-sm text-neutral-600">Loading trial balance…</p>;
  if (loadError) return <div role="alert" className="text-sm text-amber-800">
    <p>{loadError}</p>
    <button type="button" onClick={() => load()} className="mt-2 underline">Retry loading trial balance</button>
  </div>;
  if (!data) return null;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold">Trial balance</h2>
        <span className={`text-[12px] font-bold px-2 py-1 rounded-full ${data.balanced ? "bg-[#E8FFE9] text-[#0F7A2A]" : "bg-[#FFE8E8] text-[#a10404]"}`}>
          {data.balanced ? "✓ Balanced" : "Not balanced"}
        </span>
      </div>
      <div className="border border-[#eee] rounded-xl overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F4F6FB] text-[#555]"><tr><th className="text-left px-4 py-2.5">Code</th><th className="text-left px-4 py-2.5">Account</th><th className="text-left px-4 py-2.5">Type</th><th className="text-right px-4 py-2.5">Debit</th><th className="text-right px-4 py-2.5">Credit</th></tr></thead>
          <tbody>
            {data.rows.filter((r) => r.debit || r.credit).map((r) => (
              <tr key={r.account_id} className="border-t border-[#eee]" data-testid={`acc-tb-row-${r.account_code}`}>
                <td className="px-4 py-2.5 text-[#888]">{r.account_code}</td>
                <td className="px-4 py-2.5 text-[#111]">{r.account_name}</td>
                <td className="px-4 py-2.5 text-[#666]">{r.account_type}</td>
                <td className="px-4 py-2.5 text-right text-[#555]">{r.debit ? `€${fmt(r.debit)}` : "—"}</td>
                <td className="px-4 py-2.5 text-right text-[#555]">{r.credit ? `€${fmt(r.credit)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-[#F4F6FB] font-semibold">
            <tr className="border-t border-[#eee]"><td colSpan="3" className="px-4 py-2.5">Total</td><td className="px-4 py-2.5 text-right" data-testid="acc-tb-total-debit">€{fmt(data.total_debit)}</td><td className="px-4 py-2.5 text-right" data-testid="acc-tb-total-credit">€{fmt(data.total_credit)}</td></tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function PnLPanel() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, loading, loadError, appliedParams, load: loadReport } =
    useAccountingReport("pnl", readProfitAndLoss);
  const load = () => loadReport({
    ...(from ? { date_from: from } : {}),
    ...(to ? { date_to: to } : {}),
  });
  const currentPeriod = (appliedParams.date_from || "") === from &&
    (appliedParams.date_to || "") === to;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-[16px] font-semibold">Profit &amp; Loss</h2>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="text-[13px] px-2.5 py-1.5 border border-[#eee] rounded-md" placeholder="from" />
        <span className="text-[#888] text-[13px]">→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="text-[13px] px-2.5 py-1.5 border border-[#eee] rounded-md" />
        <button onClick={load} disabled={loading} className="zy-btn-primary text-[13px]" data-testid="acc-pnl-refresh">{loading ? "Loading…" : "Refresh"}</button>
        <button
          onClick={() => {
            const params = new URLSearchParams();
            if (from) params.set("date_from", from);
            if (to) params.set("date_to", to);
            const qs = params.toString();
            window.open(
              `${API}/accounting/pnl.xlsx${qs ? "?" + qs : ""}`,
              "_blank",
              "noopener,noreferrer"
            );
          }}
          className="zy-btn-primary text-[13px]"
          disabled={loading || !!loadError || !data || !currentPeriod}
          data-testid="acc-pnl-export-xlsx-btn"
          title="Export Profit & Loss as Excel (.xlsx)"
        >
          Export Excel
        </button>
      </div>
      {loading && <p role="status" className="text-sm text-neutral-600">Loading profit and loss…</p>}
      {loadError && <div role="alert" className="text-sm text-amber-800">
        <p>{loadError}</p>
        <button type="button" onClick={load} className="mt-2 underline">Retry loading profit and loss</button>
      </div>}
      {!loading && !loadError && data && !currentPeriod &&
        <p role="status" className="text-sm text-amber-800">Refresh to load the selected period before viewing or exporting figures.</p>}
      {!loading && !loadError && data && currentPeriod && (
      <div className="border border-[#eee] rounded-xl overflow-hidden">
        <table className="w-full text-[13px]">
          <tbody>
            <tr className="bg-[#F4F6FB] font-semibold"><td className="px-4 py-2" colSpan="2">Revenue</td></tr>
            {data.revenue.length === 0 ? <tr><td className="px-4 py-2.5 text-[#888]" colSpan="2">No revenue in period.</td></tr> :
              data.revenue.map((r) => (
                <tr key={r.account_id} className="border-t border-[#eee]"><td className="px-4 py-2 text-[#111]">{r.account_code} · {r.account_name}</td><td className="px-4 py-2 text-right text-[#555]">€{fmt(r.amount)}</td></tr>
              ))}
            <tr className="border-t border-[#eee] font-semibold"><td className="px-4 py-2">Total revenue</td><td className="px-4 py-2 text-right text-[#0F7A2A]" data-testid="acc-pnl-total-revenue">€{fmt(data.total_revenue)}</td></tr>
            <tr className="bg-[#F4F6FB] font-semibold border-t border-[#eee]"><td className="px-4 py-2" colSpan="2">Expenses</td></tr>
            {data.expenses.length === 0 ? <tr><td className="px-4 py-2.5 text-[#888]" colSpan="2">No expenses in period.</td></tr> :
              data.expenses.map((r) => (
                <tr key={r.account_id} className="border-t border-[#eee]"><td className="px-4 py-2 text-[#111]">{r.account_code} · {r.account_name}</td><td className="px-4 py-2 text-right text-[#555]">€{fmt(r.amount)}</td></tr>
              ))}
            <tr className="border-t border-[#eee] font-semibold"><td className="px-4 py-2">Total expenses</td><td className="px-4 py-2 text-right text-[#a10404]" data-testid="acc-pnl-total-expenses">€{fmt(data.total_expenses)}</td></tr>
            <tr className="border-t-2 border-[#111] font-bold bg-[#F4F6FB]"><td className="px-4 py-3 text-[15px]">Net income</td><td className={`px-4 py-3 text-right text-[15px] ${data.net_income >= 0 ? "text-[#0F7A2A]" : "text-[#a10404]"}`} data-testid="acc-pnl-net">€{fmt(data.net_income)}</td></tr>
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
